import { io, type Socket } from '@/libs/socketIo';
import { logger } from '@/utils';

export type ExatoPartner = {
  ex_doccheck_identity_key_id: string;
};

export type ExatoDocCheck = {
  init: () => void;
  onComplete?: (callback: () => void) => void;
};

export type UserValidationSocketResponse = {
  userId: string;
  validated: boolean;
};

export type ExatoDigitalConfig = {
  apiKey: string;
  environment: 'sandbox' | 'production';
};

declare global {
  interface Window {
    ex_partner?: ExatoPartner;
    exDocCheck?: ExatoDocCheck;
    __exatoDocCheckLoading?: boolean;
    __exatoDocCheckInitialized?: boolean;
    __exatoDocCheckInitPromise?: Promise<void>;
    __exatoDocCheckIframe?: HTMLIFrameElement | null;
  }
}

let socket: Socket | null = null;

export const getSocket = (): Socket | null => {
  if (typeof window === 'undefined') return null;

  if (!socket) {
    const baseUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001';

    socket = io(baseUrl, {
      transports: ['websocket'],
      path: '/socket.io/customer/',
      autoConnect: false,
      timeout: 6000,
      withCredentials: true,
    });

    socket.on('connect', () => {});

    socket.on('disconnect', () => {
      logger.info('Validation socket disconnected');
    });

    socket.on('connect_error', (error: unknown) => {
      logger.error('Validation socket connect error', error);
    });

    socket.on('error', (error: unknown) => {
      logger.error('Validation socket error', error);
    });
  }

  return socket;
};

export const disconnectValidationSocket = (): void => {
  if (socket && socket.connected) {
    socket.disconnect();
  }
};

export const generateCpfHash = (cpf: string): Promise<string> => {
  return new Promise((resolve) => {
    const hash = btoa(`${cpf}-${Date.now()}-validation`).substring(0, 32);

    setTimeout(() => resolve(hash), 300);
  });
};

export const hashCpfExato = (cpf: string): string => {
  const cleanCpf = cpf.replace(/[.-]/g, '');

  const timestamp = Date.now();
  const rawHash = `${cleanCpf}-${timestamp}-exato-validation`;
  const hash = btoa(rawHash)
    .replace(/[^a-zA-Z0-9]/g, '')
    .substring(0, 32);

  return hash;
};

export const cleanupExatoDocCheck = (): void => {
  if (typeof window === 'undefined') return;

  const win: Window & {
    __exatoDocCheckLoading?: boolean;
    __exatoDocCheckInitialized?: boolean;
    __exatoDocCheckInitPromise?: Promise<void>;
    __exatoDocCheckIframe?: HTMLIFrameElement | null;
  } = window;

  const iframeSelector = [
    "iframe[src*='doccheck.exato.digital']",
    "iframe[src*='exato']",
    "iframe[src*='doccheck']",
    "iframe[id*='exato']",
    "iframe[name*='exato']",
    "iframe[class*='exato']",
  ].join(',');

  const removeTopLevelInjectedNode = (node: Element) => {
    let current: Element | null = node;

    while (current?.parentElement && current.parentElement !== document.body) {
      current = current.parentElement;
    }

    if (current?.parentElement === document.body) {
      current.remove();
      return;
    }

    node.remove();
  };

  document
    .querySelectorAll(`${iframeSelector},#ex-doccheck-close`)
    .forEach((node) => removeTopLevelInjectedNode(node));

  document.querySelectorAll('[data-vaul-overlay]').forEach((el) => {
    if (el instanceof HTMLElement) {
      el.style.zIndex = '50';
    }
  });

  document.querySelectorAll('[data-vaul-drawer]').forEach((el) => {
    if (el instanceof HTMLElement) {
      el.style.zIndex = '50';
    }
  });

  win.__exatoDocCheckIframe = null;
  win.__exatoDocCheckInitialized = false;
  win.__exatoDocCheckLoading = false;
  win.__exatoDocCheckInitPromise = undefined;
};

export const injectExatoDocCheckScript = (
  publicKey: string,
  onReady?: () => void,
): void => {
  if (typeof window === 'undefined') return;

  const win: Window & {
    __exatoDocCheckLoading?: boolean;
    __exatoDocCheckInitialized?: boolean;
    __exatoDocCheckInitPromise?: Promise<void>;
    __exatoDocCheckIframe?: HTMLIFrameElement | null;
  } = window;

  logger.debug('[DocCheck] Inicializando injeção do script');

  const DOC_CHECK_IFRAME_SELECTOR = "iframe[src*='exato'],iframe[src*='doccheck']";
  const CLOSE_BUTTON_ID = 'ex-doccheck-close';

  const isMobileViewport = () => window.innerWidth <= 768;

  const normalizeZIndex = (value: string) => value;

  const removeTopLevelInjectedNode = (node: Element) => {
    let current: Element | null = node;

    while (current?.parentElement && current.parentElement !== document.body) {
      current = current.parentElement;
    }

    if (current?.parentElement === document.body) {
      current.remove();
      return;
    }

    node.remove();
  };

  const removeExatoNodes = () => {
    logger.debug('[DocCheck] Limpando iframes existentes');

    document
      .querySelectorAll(`${DOC_CHECK_IFRAME_SELECTOR},#${CLOSE_BUTTON_ID}`)
      .forEach((node) => removeTopLevelInjectedNode(node));

    win.__exatoDocCheckIframe = null;
  };

  if (win.__exatoDocCheckLoading || win.__exatoDocCheckInitPromise) {
    logger.debug('[DocCheck] Já existe um processo em andamento, abortando');
    return;
  }

  removeExatoNodes();

  win.__exatoDocCheckLoading = true;
  win.__exatoDocCheckInitialized = false;

  const adjustVaulZIndex = (reduce: boolean) => {
    const overlayZIndex = reduce ? '40' : '50';
    const drawerZIndex = reduce ? '45' : '50';

    document.querySelectorAll('[data-vaul-overlay]').forEach((el) => {
      if (el instanceof HTMLElement) {
        el.style.zIndex = normalizeZIndex(overlayZIndex);
      }
    });

    document.querySelectorAll('[data-vaul-drawer]').forEach((el) => {
      if (el instanceof HTMLElement) {
        el.style.zIndex = normalizeZIndex(drawerZIndex);
      }
    });
  };

  const ensureSingleIframe = () => {
    const iframes = Array.from(
      document.querySelectorAll(DOC_CHECK_IFRAME_SELECTOR),
    ) as HTMLIFrameElement[];

    if (iframes.length > 0) {
      const [keep, ...remove] = iframes;

      win.__exatoDocCheckIframe = keep;

      remove.forEach((node) => {
        logger.debug('[DocCheck] Removendo iframe duplicado');
        node.remove();
      });
    } else {
      win.__exatoDocCheckIframe = null;
    }
  };

  const applyCloseButtonStyles = (el: HTMLElement) => {
    el.style.zIndex = '2147483647';
    el.style.pointerEvents = 'auto';
    el.style.visibility = 'visible';
    el.style.opacity = '1';
    el.style.touchAction = 'manipulation';
    el.style.cursor = 'pointer';

    if (isMobileViewport()) {
      el.style.minWidth = '44px';
      el.style.minHeight = '44px';
    }
  };

  const ensureCloseButtonAccessible = () => {
    const closeButton = document.getElementById(CLOSE_BUTTON_ID);
    if (closeButton instanceof HTMLElement) {
      applyCloseButtonStyles(closeButton);
    }
  };

  const initialize = () => {
    logger.debug('[DocCheck] Executando initialize');

    setTimeout(() => {
      if (window.exDocCheck) {
        try {
          window.exDocCheck.init();

          win.__exatoDocCheckInitialized = true;
          win.__exatoDocCheckLoading = false;

          ensureSingleIframe();

          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              mutation.addedNodes.forEach((node) => {
                if (
                  node instanceof HTMLIFrameElement &&
                  (node.src.includes('exato') || node.src.includes('doccheck'))
                ) {
                  if (win.__exatoDocCheckIframe && win.__exatoDocCheckIframe !== node) {
                    node.remove();
                    return;
                  }

                  win.__exatoDocCheckIframe = node;

                  adjustVaulZIndex(true);

                  node.style.zIndex = normalizeZIndex('99999');

                  setTimeout(ensureCloseButtonAccessible, 100);

                  ensureSingleIframe();
                }

                if (node instanceof HTMLElement) {
                  const closeButton = node.querySelector(`#${CLOSE_BUTTON_ID}`);

                  if (closeButton instanceof HTMLElement) {
                    applyCloseButtonStyles(closeButton);
                  }
                }
              });

              ensureCloseButtonAccessible();

              mutation.removedNodes.forEach((node) => {
                if (
                  node instanceof HTMLIFrameElement &&
                  (node.src.includes('exato') || node.src.includes('doccheck'))
                ) {
                  adjustVaulZIndex(false);

                  if (win.__exatoDocCheckIframe === node) {
                    win.__exatoDocCheckIframe = null;
                  }

                  win.__exatoDocCheckInitialized = false;
                  win.__exatoDocCheckLoading = false;

                  ensureSingleIframe();
                }
              });
            });
          });

          observer.observe(document.body, {
            childList: true,
            subtree: true,
          });

          if (window.exDocCheck.onComplete) {
            window.exDocCheck.onComplete(() => {
              logger.debug('[DocCheck] Fluxo finalizado');

              observer.disconnect();

              adjustVaulZIndex(false);

              win.__exatoDocCheckInitialized = false;
              win.__exatoDocCheckLoading = false;
              win.__exatoDocCheckIframe = null;

              ensureSingleIframe();
              removeExatoNodes();
            });
          }

          onReady?.();
        } catch (err) {
          win.__exatoDocCheckLoading = false;
          logger.error('[DocCheck] init failed', err);
        }
      } else {
        win.__exatoDocCheckLoading = false;
        logger.warn('[DocCheck] exDocCheck não disponível no window');
      }
    }, 500);
  };

  win.__exatoDocCheckInitPromise = new Promise<void>((resolve) => {
    const existingScript = document.getElementById('exato-doccheck-script');

    if (existingScript) {
      logger.debug('[DocCheck] Script já existente, reutilizando');
      initialize();
      resolve();
      return;
    }

    window.ex_partner = {
      ex_doccheck_identity_key_id: publicKey,
    };

    const script = document.createElement('script');
    script.id = 'exato-doccheck-script';
    script.src = 'https://doccheck.exato.digital/doccheck.js';
    script.async = true;

    script.onload = () => {
      logger.debug('[DocCheck] Script carregado com sucesso');
      initialize();
      resolve();
      win.__exatoDocCheckInitPromise = undefined;
    };

    script.onerror = () => {
      logger.error('[DocCheck] Falha ao carregar script');
      win.__exatoDocCheckLoading = false;
      win.__exatoDocCheckInitPromise = undefined;
      resolve();
    };

    document.head.appendChild(script);
  });
};
